import { useState, createContext, useContext, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useMutation, useQuery } from "@tanstack/react-query";
import { signUpRequest } from "../api/signUp";
import { logInRequest, fetchAccountInfo } from "../api/logIn";
import { logOut as logOutRequest } from "../api/logOut";
import { refreshToken } from "../api/refreshToken";
import dayjs from "dayjs";
import { useLocalStorage } from "../hooks/useStorage";
const Context = createContext(null);
export const useAuth = () => {
  return useContext(Context);
};

const AuthContext = ({ children }) => {
  const [cookies, setCookie, removeCookie] = useCookies(["accessToken"]);
  const [isLogged, setIsLogged] = useState(false);
  const [accountInfo, setAccountInfo] = useState();
  const [sessionOver, setSessionOver] = useState(false);
  const [lang, setLang, removeLang] = useLocalStorage("lang", "en");

  const [signUpData, setSignUpData] = useState({
    isAcceptTerms: false,
    email: "",
    password: "",
    role: "",
    person: {
      firstname: "",
      lastname: "",
      gender: "",
      birthdate: "",
      cityId: "",
      phoneNumber: "",
    },
  });
  const [signUpStep, setSignUpStep] = useState(1);
  const [signUpErrors, setSignUpErrors] = useState({
    email: null,
    emailForm: null,
    isEmailUsed: false,
    phoneNumber: null,
    phoneNumberForm: null,
    passwordForm: null,
    password: null,
    unkown: false,
  });
  const signUp = useMutation({
    mutationFn: () => signUpRequest(signUpData),
    onError: (error) => errorHandler(error.response.data.error),
    onSuccess: () =>
      logIn.mutate({ email: signUpData.email, password: signUpData.password }),
  });
  const errorHandler = (error) => {
    if (error === "email exists") {
      setSignUpErrors((prevSignUpErrors) => ({
        ...prevSignUpErrors,
        isEmailUsed: true,
      }));
    } else {
      setSignUpErrors((prevSignUpErrors) => ({
        ...prevSignUpErrors,
        unkown: error,
      }));
    }
  };

  const [logInData, setLogInData] = useState({
    email: "",
    password: "",
  });
  const logIn = useMutation({
    mutationFn: (loginCredientials) =>
      logInRequest({
        email: loginCredientials.email,
        password: loginCredientials.password,
      }),
    onSuccess: (data) => handleLoginSuccess(data),
    onError: (error) => handleApiErr(error.response.status),
  });
  const handleLoginSuccess = async (data) => {
    setCookie("accessToken", data.accessToken, {
      path: "/",
      expires: dayjs().add(30, "day").toDate(),
      sameSite: "strict",
    });
    setLogInData({
      email: "",
      password: "",
    });
  };
  const [errors, setErrors] = useState({
    email: null,
    password: null,
    apiErr: null,
  });
  const handleApiErr = (error) => {
    if (error < 500) {
      setErrors({ ...errors, apiErr: "Email and Password don't match!" });
    } else {
      setErrors({
        ...errors,
        apiErr: "Something went wrong. please try again later",
      });
    }
  };

  const reFetchAccessToken = useQuery({
    queryKey: ["refetchToken"],
    queryFn: () => refreshToken(),
    onSuccess: (data) => {
      setCookie("accessToken", data.accessToken, {
        path: "/",
        expires: dayjs().add(30, "day").toDate(),
        sameSite: "strict",
      });
    },
    onError: () => logOut(),
    refetchInterval: 5 * 60000, // fetch data every 5 minutes
    enabled:
      cookies.accessToken && cookies.accessToken !== undefined ? true : false,
    retry: 2,
  });

  const getAccountInfo = useQuery({
    queryKey: ["accountInfo"],
    queryFn: () => fetchAccountInfo(cookies.accessToken),
    onSuccess: (data) => {
      setAccountInfo(data);
      setIsLogged(true);
    },
    onError: () => setIsLogged(false),
    enabled:
      cookies.accessToken && cookies.accessToken !== undefined ? true : false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const logOut = () => {
    removeCookie("accessToken");
    logOutRequest();
    setIsLogged(false);
    setAccountInfo(null);
  };
  useEffect(() => {
    if (accountInfo && accountInfo !== null) {
      setIsLogged(true);
    }
  }, [accountInfo]);

  useEffect(() => {
    if (cookies.accessToken) getAccountInfo.refetch();
  }, [cookies]);

  const value = {
    signUpStep,
    setSignUpStep,
    signUpData,
    setSignUpData,
    signUp,
    signUpErrors,
    reFetchAccessToken,
    setSignUpErrors,
    isLogged,
    logIn,
    errors,
    setErrors,
    setLogInData,
    logInData,
    getAccountInfo,
    lang,
    setLang,
    accountInfo,
    setSessionOver,
    sessionOver,
    setIsLogged,
    logOut,
  };
  return <Context.Provider value={value}>{children}</Context.Provider>;
};
export default AuthContext;
